import { useState, useEffect, useCallback } from 'react'
import { BusinessParticipant, CapitalContribution, CapitalSettings } from '@/lib/db/schema'
import { CreateParticipantInput, UpdateParticipantInput, CreateContributionInput, UpdateContributionInput, UpdateCapitalSettingsInput } from '@/lib/validation/capital'
import { CapitalSummary } from '@/lib/services/capital'

export interface CapitalData {
  settings: CapitalSettings
  participants: BusinessParticipant[]
  contributions: CapitalContribution[]
  metrics: CapitalSummary
}

export function useCapital(businessId: string | undefined) {
  const [data, setData] = useState<CapitalData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSummary = useCallback(async () => {
    if (!businessId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/businesses/${businessId}/capital/summary`)
      const json = await res.json() as any
      if (res.ok && json.success) {
        setData(json.data)
      } else {
        setError(json.error?.message || 'Failed to fetch capital summary')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
    } finally {
      setIsLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  // --- Mutators ---

  const updateSettings = async (payload: UpdateCapitalSettingsInput) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/capital/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSummary()
      return { success: true }
    }
    return { success: false, error: json.error?.message || 'Update failed' }
  }

  const addParticipant = async (payload: CreateParticipantInput) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/capital/participants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSummary()
      return { success: true }
    }
    return { success: false, error: json.error?.message || 'Add participant failed' }
  }

  const updateParticipant = async (participantId: string, payload: UpdateParticipantInput) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/capital/participants/${participantId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSummary()
      return { success: true }
    }
    return { success: false, error: json.error?.message || 'Update participant failed' }
  }

  const addContribution = async (payload: CreateContributionInput) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/capital/contributions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSummary()
      return { success: true }
    }
    return { success: false, error: json.error?.message || 'Add contribution failed' }
  }

  const updateContributionStatus = async (contributionId: string, status: string) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/capital/contributions/${contributionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSummary()
      return { success: true }
    }
    return { success: false, error: json.error?.message || 'Update contribution failed' }
  }

  return { 
    data, 
    isLoading, 
    error, 
    refetch: fetchSummary, 
    updateSettings, 
    addParticipant,
    updateParticipant,
    addContribution,
    updateContributionStatus
  }
}
